import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Trash2, Loader2 } from "lucide-react";
import {
    adminWaitlist, adminContacts, adminDeleteWaitlist, adminDeleteContact,
    formatApiError,
} from "../../lib/adminApi";
import { formatDate } from "../../lib/updates";

/**
 * Generic list page rendering either waitlist or contact submissions.
 * Used as <AdminTablePage kind="waitlist" /> or kind="contacts".
 */
export default function AdminTablePage({ kind }) {
    const config = TABLE_CONFIG[kind];
    const [rows, setRows] = useState(null);
    const [query, setQuery] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        const fetcher = kind === "waitlist" ? adminWaitlist : adminContacts;
        fetcher()
            .then(setRows)
            .catch(() => setRows([]));
    }, [kind]);

    const handleDelete = async (row) => {
        const id = row.id;
        if (!id) {
            toast.error("Kan ikke slette: række mangler ID");
            return;
        }
        const label = row.name || row.email || id;
        const confirmed = window.confirm(
            kind === "waitlist"
                ? `Slet tilmelding fra "${label}"?\n\nDette kan ikke fortrydes.`
                : `Slet henvendelse fra "${label}"?\n\nDette kan ikke fortrydes.`
        );
        if (!confirmed) return;
        setDeletingId(id);
        try {
            const deleter = kind === "waitlist" ? adminDeleteWaitlist : adminDeleteContact;
            await deleter(id);
            setRows((prev) => (prev || []).filter((r) => r.id !== id));
            toast.success(kind === "waitlist" ? "Tilmelding slettet" : "Henvendelse slettet");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = useMemo(() => {
        if (!rows) return null;
        if (!query.trim()) return rows;
        const q = query.toLowerCase();
        return rows.filter((r) =>
            JSON.stringify(r).toLowerCase().includes(q),
        );
    }, [rows, query]);

    const exportCsv = () => {
        if (!rows || rows.length === 0) {
            toast.error("Ingen data at eksportere");
            return;
        }
        const cols = config.columns.map((c) => c.key);
        const header = cols.join(",");
        const escape = (v) => {
            if (v == null) return "";
            const s = String(v).replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
        };
        const lines = rows.map((r) => cols.map((c) => escape(r[c])).join(","));
        const csv = [header, ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `cleany-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${rows.length} rækker eksporteret`);
    };

    return (
        <div data-testid={`admin-${kind}-page`}>
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
                        {config.title}
                    </h1>
                    <p className="mt-1 text-slate-600">{config.subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Søg…"
                            data-testid={`admin-${kind}-search`}
                            className="h-10 pl-9 pr-3 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 focus:outline-none text-sm w-56"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={exportCsv}
                        data-testid={`admin-${kind}-export`}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 px-4 h-10 text-sm font-semibold transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        CSV
                    </button>
                </div>
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden">
                {filtered === null ? (
                    <div className="p-10 text-center text-slate-500">Henter…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-slate-500" data-testid={`admin-${kind}-empty`}>
                        {rows && rows.length > 0
                            ? "Ingen rækker matcher søgningen."
                            : `Ingen ${kind === "waitlist" ? "tilmeldinger" : "henvendelser"} endnu.`}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    {config.columns.map((c) => (
                                        <th key={c.key} className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider whitespace-nowrap">
                                            {c.label}
                                        </th>
                                    ))}
                                    <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider whitespace-nowrap w-20">
                                        Handling
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <tr key={r.id || JSON.stringify(r)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                                        {config.columns.map((c) => (
                                            <td key={c.key} className="px-4 py-3 text-slate-700 align-top">
                                                {c.render
                                                    ? c.render(r[c.key], r)
                                                    : (r[c.key] ?? <span className="text-slate-300">—</span>)}
                                            </td>
                                        ))}
                                        <td className="px-4 py-3 text-right align-top">
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(r)}
                                                disabled={deletingId === r.id}
                                                data-testid={`admin-${kind}-delete-${r.id}`}
                                                aria-label="Slet"
                                                title="Slet"
                                                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {deletingId === r.id
                                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                                    : <Trash2 className="h-4 w-4" />}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const TABLE_CONFIG = {
    waitlist: {
        title: "Waitlist",
        subtitle: "Alle pre-launch tilmeldinger — nyeste først.",
        columns: [
            { key: "created_at", label: "Dato", render: (v) => v ? formatDate(v.slice(0, 10)) : "" },
            { key: "name", label: "Navn" },
            { key: "company_name", label: "Firma" },
            { key: "email", label: "Email", render: (v) => v ? <a href={`mailto:${v}`} className="text-sky-700 hover:underline">{v}</a> : "" },
            { key: "phone", label: "Telefon" },
            { key: "referral_code", label: "Ref kode", render: (v) => v ? <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v}</code> : "" },
            { key: "referred_by", label: "Henvist af", render: (v) => v ? <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v}</code> : "" },
        ],
    },
    contacts: {
        title: "Kontakthenvendelser",
        subtitle: "Beskeder sendt via kontaktformularen.",
        columns: [
            { key: "created_at", label: "Dato", render: (v) => v ? formatDate(v.slice(0, 10)) : "" },
            { key: "name", label: "Navn" },
            { key: "email", label: "Email", render: (v) => v ? <a href={`mailto:${v}`} className="text-sky-700 hover:underline">{v}</a> : "" },
            { key: "phone", label: "Telefon" },
            { key: "message", label: "Besked", render: (v) => v ? <span className="line-clamp-3 max-w-md inline-block">{v}</span> : "" },
        ],
    },
};
